import * as React from 'react';
import ReactCrop, { PixelCrop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { uploadImage } from 'Src/helpers/service/image';
import { AppUser } from 'Src/views/App/App';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';

interface Props {
  user: AppUser;
  values: string[];
  onChange: (urls: string[]) => void;
  onError: () => void;
  entityName?: string;
}

export function TournamentCoverUploader({ user, values, onChange, onError, entityName = 'tournament' }: Props) {
  const [source, setSource] = React.useState('');
  const [fileName, setFileName] = React.useState('tournament-cover.jpg');
  const [crop, setCrop] = React.useState<PercentCrop>({ unit: '%', x: 5, y: 35, width: 90, height: 30, aspect: 3 });
  const [completed, setCompleted] = React.useState<PixelCrop | null>(null);
  const [image, setImage] = React.useState<HTMLImageElement | null>(null);
  const [uploading, setUploading] = React.useState(false);

  const close = () => {
    if (source) URL.revokeObjectURL(source);
    setSource('');
    setCompleted(null);
    setImage(null);
  };

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setFileName(file.name);
    setSource(URL.createObjectURL(file));
  };

  const apply = async () => {
    if (!image || !completed?.width || !completed?.height) return;
    setUploading(true);
    try {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const canvas = document.createElement('canvas');
      canvas.width = 1500;
      canvas.height = 500;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas is unavailable');
      context.drawImage(
        image,
        completed.x * scaleX,
        completed.y * scaleY,
        completed.width * scaleX,
        completed.height * scaleY,
        0,
        0,
        1500,
        500
      );
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          result => (result ? resolve(result) : reject(new Error('Unable to crop image'))),
          'image/jpeg',
          0.82
        )
      );
      const resized = new File([blob], fileName.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      const response = await uploadImage(user, resized);
      onChange([...values, `${window.apiImg}/images/${response.key}`]);
      close();
    } catch (error) {
      onError();
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="utCreateWizard__coverUpload">
        <div className="utCreateWizard__coverGrid">
          {values.map((url, index) => (
            <article key={`${url}_${index}`}>
              <img src={url} alt={`${entityName} cover ${index + 1}`} />
              {index === 0 ? <strong>Primary</strong> : null}
              <footer>
                <button
                  type="button"
                  disabled={index === 0}
                  aria-label="Move cover left"
                  onClick={() => {
                    const next = [...values];
                    [next[index - 1], next[index]] = [next[index], next[index - 1]];
                    onChange(next);
                  }}
                >
                  ←
                </button>
                <button
                  type="button"
                  disabled={index === values.length - 1}
                  aria-label="Move cover right"
                  onClick={() => {
                    const next = [...values];
                    [next[index], next[index + 1]] = [next[index + 1], next[index]];
                    onChange(next);
                  }}
                >
                  →
                </button>
                <button type="button" onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}>
                  Remove
                </button>
              </footer>
            </article>
          ))}
          <label className="utCreateWizard__coverAdd">
            <span>+</span>
            <b>{values.length ? 'Add another cover' : 'Add cover photo'}</b>
            <small>Crop to 3:1 before upload</small>
            <input type="file" accept="image/*" onChange={choose} />
          </label>
        </div>
        <p>
          The first image is the primary cover. Change the order using the arrow controls; the public website displays all
          covers as a slider.
        </p>
      </div>
      <SimpleModal
        isOpen={Boolean(source)}
        title={`Crop ${entityName} cover`}
        showFooter={false}
        onCloseClick={close}
        dialogStyle={{ maxWidth: 820 }}
      >
        <div className="utCreateWizard__cropModal">
          <p>The public site uses a 3:1 banner. The saved image is resized to 1500 × 500px.</p>
          {source ? (
            <ReactCrop
              src={source}
              crop={crop}
              keepSelection
              imageAlt="Crop preview"
              onImageLoaded={nextImage => setImage(nextImage)}
              onChange={(_, percent) => setCrop(percent as PercentCrop)}
              onComplete={pixel => setCompleted(pixel as PixelCrop)}
            />
          ) : null}
          <footer>
            <button type="button" onClick={close}>
              Cancel
            </button>
            <button type="button" className="is-primary" disabled={!completed || uploading} onClick={apply}>
              {uploading ? 'Uploading…' : 'Apply crop'}
            </button>
          </footer>
        </div>
      </SimpleModal>
    </>
  );
}
